import { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { bool, elementType, func, oneOf, string } from 'prop-types';

import ChevronLeftButton from '~/components/ButtonIcons/ChevronLeftButton';
import CloseBorderedButton from '~/components/ButtonIcons/CloseBorderedButton';
import PopupAbstract, {
  propTypes as popupAbstractPropTypes,
} from '~/components/PopupAbstract';

import useOnClose from '~/hooks/useOnClose';
import { HistoryCounterContext } from '~/routes/HistoryCounter';

import styles from './PopupPlain.module.scss';

export const effectsClassNames = {
  OPACITY_TRANSITION: 'opacityTransition',
  BOTTOM_TRANSITION: 'bottomTransition',
};

const PopupPlain = ({
  children,
  className,
  closeAnimation,
  containerClassName,
  buttonClassName,
  onClose,
  dismissible,
  effectsClassName = effectsClassNames.OPACITY_TRANSITION,
  fullscreen,
  Button,
  ...props
}) => {
  const location = useLocation();
  const [dismissed, setDismissed] = useState(false);
  const canBack = useContext(HistoryCounterContext) > 0;
  const onCloseFallback = useOnClose();

  useEffect(() => {
    setDismissed(false);
  }, [location]);

  const onDismiss = useCallback(() => {
    if (closeAnimation) {
      closeAnimation();
    }

    if (dismissible && !dismissed) {
      setDismissed(true);
      if (onClose) {
        onClose();
      } else {
        onCloseFallback();
      }
    }
  }, [closeAnimation, dismissible, dismissed, onClose, onCloseFallback]);

  const ButtonToRender =
    Button || (onClose || !canBack ? CloseBorderedButton : ChevronLeftButton);

  return (
    <PopupAbstract
      className={classNames(
        styles.popupContainer,
        styles[effectsClassName],
        { [styles.fullScreen]: fullscreen },
        containerClassName,
      )}
      onClose={onDismiss}
      {...props}
    >
      <div className={classNames(styles.popupContent, className)}>
        {children}
      </div>
      {dismissible && (
        <ButtonToRender
          className={classNames(styles.dismissButton, buttonClassName)}
          onClick={onDismiss}
        />
      )}
      <div className={classNames(styles.animationOverlay)}></div>
    </PopupAbstract>
  );
};

export const propTypes = {
  ...popupAbstractPropTypes,
  buttonClassName: string,
  dismissible: bool,
  big: bool,
  closeAnimation: func,
  effectsClassName: oneOf(Object.keys(effectsClassNames)),
  fullscreen: bool,
  Button: elementType,
};

PopupPlain.propTypes = propTypes;

PopupPlain.defaultProps = {
  dismissible: true,
  fullscreen: true,
};

export default PopupPlain;
